/**
 * Default layout for the Acquisition page.
 *
 * Replicates the hardcoded layout:
 * - 3 MetricCards (Cash Collected, Revenue, Close Rate)
 * - 2 StatCards (Calls Due, Calls Taken)
 * - 5 StatCards (Calls Closed, Show Rate, Close Rate, Avg Cash/Call, Avg Cash/Close)
 * - 3 AdditionalStats (New Cash, MRR, Total Gross Revenue)
 * - 3 full-width pieCharts (Country, Source, Deal Status)
 * - 1 table (Recent Calls)
 */

import type { DashboardLayout } from "../types";

export const ACQUISITION_DEFAULT: DashboardLayout = {
  widgets: [
    // --- Row 1: Metric Cards (y:0) ---
    {
      id: "acq-metric-cash",
      type: "metric",
      title: "Cash Collected",
      config: {
        metricKey: "totalCashCollected",
        format: "currency",
        icon: "dollar",
        color: "brand",
      },
    },
    {
      id: "acq-metric-revenue",
      type: "metric",
      title: "Revenue",
      config: {
        metricKey: "totalRevenue",
        format: "currency",
        icon: "trending",
        color: "green",
      },
    },
    {
      id: "acq-metric-closerate",
      type: "metric",
      title: "Close Rate",
      config: {
        metricKey: "closeRate",
        format: "percent",
        icon: "chart",
        color: "blue",
      },
    },

    // --- Row 2: Call Stats (y:4) ---
    {
      id: "acq-stat-callsdue",
      type: "stat",
      title: "Calls Due",
      config: { metricKey: "callsDue", format: "number" },
    },
    {
      id: "acq-stat-callstaken",
      type: "stat",
      title: "Calls Taken",
      config: { metricKey: "callsTaken", format: "number" },
    },

    // --- Row 3: Call Results (y:6) ---
    {
      id: "acq-stat-callsclosed",
      type: "stat",
      title: "Calls Closed",
      config: { metricKey: "callsClosed", format: "number" },
    },
    {
      id: "acq-stat-showrate",
      type: "stat",
      title: "Show Rate",
      config: { metricKey: "showRate", format: "percent" },
    },
    {
      id: "acq-stat-closerate",
      type: "stat",
      title: "Close Rate",
      config: { metricKey: "closeRate", format: "percent" },
    },
    {
      id: "acq-stat-avgcashcall",
      type: "stat",
      title: "Avg. Cash / Call",
      config: { metricKey: "avgCashPerCall", format: "currency" },
    },
    {
      id: "acq-stat-avgcashclose",
      type: "stat",
      title: "Avg. Cash / Close",
      config: { metricKey: "avgCashPerClose", format: "currency" },
    },

    // --- Row 4: Additional Stats (y:8) ---
    {
      id: "acq-addstat-newcash",
      type: "additionalStat",
      title: "New Cash Collected",
      config: {
        metricKey: "newCashCollected",
        format: "currency",
        description: "Total cash from new deals (excludes MRR)",
      },
    },
    {
      id: "acq-addstat-mrr",
      type: "additionalStat",
      title: "MRR",
      config: {
        metricKey: "mrrCash",
        format: "currency",
        description: "Monthly recurring revenue",
      },
    },
    {
      id: "acq-addstat-grossrev",
      type: "additionalStat",
      title: "Total Gross Revenue",
      config: {
        metricKey: "totalGrossRevenue",
        format: "currency",
        description: "Total contract value (all revenue)",
      },
    },

    // --- Row 5: Country Pie Chart (y:12) ---
    {
      id: "acq-pie-country",
      type: "pieChart",
      title: "Closes by Country",
      config: { dataKey: "countryData", title: "Closes by Country" },
    },

    // --- Row 6: Source Pie Chart (y:26) ---
    {
      id: "acq-pie-source",
      type: "pieChart",
      title: "Closes by Source",
      config: { dataKey: "leadSourceData", title: "Closes by Source" },
    },

    // --- Row 7: Deal Status Pie Chart (y:40) ---
    {
      id: "acq-pie-dealstatus",
      type: "pieChart",
      title: "Deal Status Breakdown",
      config: { dataKey: "outcomeBreakdown", title: "Deal Status Breakdown" },
    },

    // --- Row 8: Table (y:54) ---
    {
      id: "acq-table-recentcalls",
      type: "table",
      title: "Recent Calls",
      config: { dataKey: "recentCalls", title: "Recent Calls" },
    },
  ],

  gridLayout: [
    // Row 1: 3 metric cards
    { i: "acq-metric-cash", x: 0, y: 0, w: 4, h: 3 },
    { i: "acq-metric-revenue", x: 4, y: 0, w: 4, h: 3 },
    { i: "acq-metric-closerate", x: 8, y: 0, w: 4, h: 3 },

    // Row 2: 2 stat cards
    { i: "acq-stat-callsdue", x: 0, y: 3, w: 6, h: 2 },
    { i: "acq-stat-callstaken", x: 6, y: 3, w: 6, h: 2 },

    // Row 3: 5 stat cards (2+2+3+3+2 = 12)
    { i: "acq-stat-callsclosed", x: 0, y: 5, w: 2, h: 2 },
    { i: "acq-stat-showrate", x: 2, y: 5, w: 2, h: 2 },
    { i: "acq-stat-closerate", x: 4, y: 5, w: 3, h: 2 },
    { i: "acq-stat-avgcashcall", x: 7, y: 5, w: 3, h: 2 },
    { i: "acq-stat-avgcashclose", x: 10, y: 5, w: 2, h: 2 },

    // Row 4: 3 additional stats
    { i: "acq-addstat-newcash", x: 0, y: 7, w: 4, h: 3 },
    { i: "acq-addstat-mrr", x: 4, y: 7, w: 4, h: 3 },
    { i: "acq-addstat-grossrev", x: 8, y: 7, w: 4, h: 3 },

    // Row 5: full-width pie chart (Country)
    { i: "acq-pie-country", x: 0, y: 10, w: 12, h: 14 },

    // Row 6: full-width pie chart (Source)
    { i: "acq-pie-source", x: 0, y: 24, w: 12, h: 14 },

    // Row 7: full-width pie chart (Deal Status)
    { i: "acq-pie-dealstatus", x: 0, y: 38, w: 12, h: 14 },

    // Row 8: table
    { i: "acq-table-recentcalls", x: 0, y: 52, w: 12, h: 16 },
  ],
};
